import * as React from "react";
import { TextInput } from "react-admin";
import { BKFCourse } from "../../providers/coursesProvider";
import { Row } from "../../misc/Row";
import { LocalizedError } from "../../utils/LocalizedError";
import { atom, useRecoilValue } from "recoil";
import { Column } from "../../misc/Column";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Form2Theme } from "../../misc/Form2";
import { LecturerInput } from "../../inputs/LecturerInput";
import { RoomInput } from "../../inputs/RoomInput";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { getAuthenticatedServerClient } from "../../api/server.api";
import { grants } from "../../backoffice.access_control";
import { ConfirmDialog } from "../ConfirmDialog";
import { useDialog } from "../../hooks/useDialog";

export const bulkEditSelectedEventsIdsAtom = atom<Set<string>>({
  key: "bulkEditSelectedEventsIdsAtom",
  default: new Set(),
});

export interface BulkEditFormValues {
  lecturerId: string;
  roomId: string;
  noteForParticipants: string;
}

export const bulkEditDefaultFormValues: BulkEditFormValues = {
  lecturerId: "__DO_NOT_CHANGE__",
  roomId: "__DO_NOT_CHANGE__",
  noteForParticipants: "",
};

export function BulkEditForm(props: { course: BKFCourse; cancelBulkEdit: () => void }) {
  const { course, cancelBulkEdit } = props;
  const selectedEventsIds = useRecoilValue(bulkEditSelectedEventsIdsAtom);
  const numSelectedEvents = selectedEventsIds.size;
  const { dialogProps: confirmDeleteDialogProps, openDialog: openConfirmDeleteDialog } = useDialog();
  const deleteTitle = `${numSelectedEvents} ${numSelectedEvents === 1 ? "Termin" : "Termine"} löschen`;

  const bulkDelete = () =>
    runSafely(
      async () => {
        const serverClient = await getAuthenticatedServerClient();
        let numDeletedEvents = 0;
        // Delete events in chronological order ...
        for (const event of course.events
          .filter((it) => selectedEventsIds.has(it.id))
          .sort((a, b) => a.start.valueOf() - b.start.valueOf())) {
          try {
            await serverClient.deleteCalendarEvent(undefined, { params: { eventId: event.id } });
            ++numDeletedEvents;
          } catch (error) {
            console.error("Error during bulk delete", error);
            if (numDeletedEvents === 0) {
              throw error;
            } else {
              throw new LocalizedError(
                numDeletedEvents === 1
                  ? `Es wurde nur 1 von ${numSelectedEvents} Terminen gelöscht.`
                  : `Es wurden nur ${numDeletedEvents} von ${numSelectedEvents} Terminen gelöscht.`,
              );
            }
          }
        }
      },
      {
        errorMessage: "Fehler beim Löschen",
        successMessage: `${numSelectedEvents === 1 ? "Termin" : `${numSelectedEvents} Termine`} erfolgreich gelöscht.`,
        onSuccess: () => {
          confirmDeleteDialogProps.onClose();
          cancelBulkEdit();
        },
      },
    );

  return (
    <Form2Theme>
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Column>
          <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">
              <span>
                {numSelectedEvents === 0
                  ? "Termine auswählen"
                  : `${numSelectedEvents}\u00A0${numSelectedEvents === 1 ? "Termin" : "Termine"} bearbeiten`}
              </span>
            </Typography>
            <IconButton onClick={cancelBulkEdit} aria-label="Massenbearbeitung beenden">
              <CloseIcon sx={{ color: "text.secondary" }} />
            </IconButton>
          </Row>
          <Typography variant="body2" sx={{ my: 2, color: "text.secondary" }}>
            Im Kalender Termine auswählen und im nächsten Schritt Änderungen festlegen.
          </Typography>
          {numSelectedEvents >= 1 && (
            <>
              <LecturerInput source="lecturerId" bulkEdit size="small" helperText={"\u00A0"} />
              <RoomInput source="roomId" bulkEdit branchId={course.branchId} size="small" helperText={"\u00A0"} />
              <TextInput
                source="noteForParticipants"
                label="Hinweis für Teilnehmer"
                multiline
                rows={2}
                size="small"
                placeholder="nicht ändern"
                sx={{ "& textarea::placeholder": { fontStyle: "italic" } }}
              />
              <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  {grants.has("CourseLesson:delete") && (
                    <Tooltip title={deleteTitle}>
                      <Button
                        color="error"
                        onClick={openConfirmDeleteDialog}
                        aria-label={deleteTitle}
                        startIcon={<DeleteOutlineIcon />}
                      >
                        Löschen
                      </Button>
                    </Tooltip>
                  )}
                </div>
                <SaveButton2 />
              </Row>
            </>
          )}
          {grants.has("CourseLesson:delete") && numSelectedEvents >= 1 && (
            <ConfirmDialog
              {...confirmDeleteDialogProps}
              title={`${deleteTitle}?`}
              message={`Bist du dir sicher, dass ${numSelectedEvents === 1 ? "der Termin gelöscht werden soll" : "die Termine gelöscht werden sollen"}? Diese Aktion kann nicht rückgängig gemacht werden.`}
              confirmButtonLabel={numSelectedEvents === 1 ? "Ja, Termin löschen" : "Ja, Termine löschen"}
              onConfirm={bulkDelete}
            />
          )}
        </Column>
      </Box>
    </Form2Theme>
  );
}
